import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchAccommodations } from '../lib/cms';

const formatPrice = (priceStr) => {
  if (!priceStr) return '';
  // Remove "per nat", "pr. nat", "/ nat", "/nat", "per natt" etc (case insensitive)
  let cleaned = priceStr.replace(/\s*(per|pr\.?|\/)\s*nat(t)?/gi, '').trim();
  
  // If it's a numeric price like "fra 3.500 DKK" but doesn't have ",00", let's make sure it has ",00"
  if (cleaned.includes('DKK') && !cleaned.includes(',00') && !cleaned.includes(',')) {
    cleaned = cleaned.replace(/(\d+[\.\d]*)\s*DKK/g, '$1,00 DKK');
  }
  return cleaned;
};

export default function Accommodations() {
  const [pageData, setPageData] = useState(null);
  const [accommodations, setAccommodations] = useState([]);
  const [filteredAccs, setFilteredAccs] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const [selectedType, setSelectedType] = useState('Alle');
  const [selectedCapacity, setSelectedCapacity] = useState('Alle');
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(true);

  const activeFiltersCount = 
    (selectedType !== 'Alle' ? 1 : 0) + 
    (selectedCapacity !== 'Alle' ? 1 : 0) + 
    selectedFacilities.length;

  // Detail Modal state
  const [selectedAcc, setSelectedAcc] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const facilityOptions = ['Wi-Fi', 'Bad', 'Køkken', 'Brændeovn', 'Toilet', 'Varmepumpe'];

  useEffect(() => {
    async function loadData() {
      const page = await fetchPageBySlug('feriehuse');
      setPageData(page);

      const data = await fetchAccommodations();
      setAccommodations(data);
      setFilteredAccs(data);

      // Check if a specific accommodation is selected via query param
      const valgtSlug = searchParams.get('valgt');
      if (valgtSlug) {
        const found = data.find(a => a.slug?.da === valgtSlug || a.slug === valgtSlug);
        if (found) {
          setSelectedAcc(found);
        }
      }

      // Check for type query param
      const typeParam = searchParams.get('type');
      if (typeParam) {
        setSelectedType(typeParam);
      }
    }
    loadData();
  }, [searchParams]);

  // Apply filters
  useEffect(() => {
    let result = accommodations;

    // Filter by Type
    if (selectedType !== 'Alle') {
      const types = selectedType.split(',');
      result = result.filter(a => {
        const itemType = a.type?.da || a.type;
        return types.includes(itemType);
      });
    }

    // Filter by Capacity
    if (selectedCapacity !== 'Alle') {
      if (selectedCapacity === '2+') {
        result = result.filter(a => a.capacity >= 2);
      } else if (selectedCapacity === '4+') {
        result = result.filter(a => a.capacity >= 4);
      } else if (selectedCapacity === '10+') {
        result = result.filter(a => a.capacity >= 10);
      }
    }

    // Filter by Facilities
    if (selectedFacilities.length > 0) {
      result = result.filter(a => {
        const facText = (a.facilities?.da || a.facilities || '').toLowerCase();
        return selectedFacilities.every(fac => facText.includes(fac.toLowerCase()));
      });
    }

    setFilteredAccs(result);
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [selectedType, selectedCapacity, selectedFacilities, accommodations]);

  const handleFacilityChange = (fac) => {
    if (selectedFacilities.includes(fac)) {
      setSelectedFacilities(selectedFacilities.filter(f => f !== fac));
    } else {
      setSelectedFacilities([...selectedFacilities, fac]);
    }
  };

  const openDetailModal = (acc) => {
    setSelectedAcc(acc);
    setActiveImageIndex(0);
    setSearchParams({ valgt: acc.slug?.da || acc.slug });
  };

  const closeDetailModal = () => {
    setSelectedAcc(null);
    setActiveImageIndex(0);
    setSearchParams({});
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredAccs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAccs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 250, behavior: 'smooth' });
  };

  const metaTitle = pageData?.meta_title?.da || "Feriehuse & Hytter på Samsø - Campen Camping";
  const metaDesc = pageData?.meta_description?.da || "Se vores store udvalg af feriehuse, hytter og campingvogne til udlejning på Samsø. Plads til 2-24 personer med fantastisk havudsigt.";

  return (
    <div className="py-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
            Vores enheder
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#0f0d0e] mt-1 mb-4">
            Feriehuse, hytter & pladser
          </h1>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed">
            Uanset om du vil bo i luksuriøse omgivelser med plads til hele familien, i en hyggelig retro hytte eller i din egen campingvogn, har vi den perfekte base til din Samsø-ferie.
          </p>
        </div>

        {/* Mobile Filter Toggle Button */}
        <button
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="lg:hidden w-full bg-white border border-stone-200 text-stone-800 font-semibold py-3 px-4 rounded-xl flex items-center justify-between shadow-sm mb-6 cursor-pointer hover:bg-stone-50 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm">
            <i className="fa-solid fa-sliders text-[#67aedd]" />
            {showMobileFilters ? 'Skjul filtrering' : 'Vis filtrering'}
          </span>
          {activeFiltersCount > 0 ? (
            <span className="bg-[#67aedd] text-white text-xs px-2.5 py-0.5 rounded-full font-bold">
              {activeFiltersCount} aktive
            </span>
          ) : (
            <i className={`fa-solid ${showMobileFilters ? 'fa-chevron-up' : 'fa-chevron-down'} text-stone-400 text-xs`} />
          )}
        </button>

        {/* Filter Panel & Results Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className={`lg:block ${showMobileFilters ? 'block' : 'hidden'} bg-white p-6 rounded-2xl border border-stone-200/60 shadow-sm h-fit lg:sticky lg:top-24`}>
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone-100">
              <h3 className="font-serif font-bold text-[#0f0d0e] text-lg">
                <i className="fa-solid fa-sliders mr-2 text-[#67aedd]" /> Filtrering
              </h3>
              {(selectedType !== 'Alle' || selectedCapacity !== 'Alle' || selectedFacilities.length > 0) && (
                <button
                  onClick={() => {
                    setSelectedType('Alle');
                    setSelectedCapacity('Alle');
                    setSelectedFacilities([]);
                  }}
                  className="text-xs text-[#67aedd] hover:text-[#5aa1cc] font-semibold underline"
                >
                  Nulstil
                </button>
              )}
            </div>

            {/* Type Filter */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-2.5">
                Type
              </label>
              <div className="flex flex-col gap-2">
                {['Alle', 'Feriehus', 'Hytte', 'Campingvogn', 'Plads', 'Event'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`text-left px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                      (selectedType === type || (type !== 'Alle' && selectedType !== 'Alle' && selectedType.split(',').includes(type)))
                        ? 'bg-[#67aedd] text-white shadow-sm'
                        : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Capacity Filter */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-2.5">
                Kapacitet
              </label>
              <div className="flex flex-col gap-2">
                {[
                  { label: 'Alle', value: 'Alle' },
                  { label: 'Min. 2 personer', value: '2+' },
                  { label: 'Min. 4 personer', value: '4+' },
                  { label: 'Min. 10 personer', value: '10+' },
                ].map((cap) => (
                  <button
                    key={cap.value}
                    onClick={() => setSelectedCapacity(cap.value)}
                    className={`text-left px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedCapacity === cap.value
                        ? 'bg-[#67aedd] text-white shadow-sm'
                        : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                    }`}
                  >
                    {cap.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Facilities Filter */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-2.5">
                Faciliteter
              </label>
              <div className="flex flex-col gap-2.5">
                {facilityOptions.map((fac) => (
                  <label key={fac} className="flex items-center gap-3 text-sm text-stone-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedFacilities.includes(fac)}
                      onChange={() => handleFacilityChange(fac)}
                      className="rounded border-stone-300 text-[#67aedd] focus:ring-[#67aedd] h-4.5 w-4.5"
                    />
                    <span>{fac}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Results Grid */}
          <div className="lg:col-span-3">
            {filteredAccs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200/60 p-12 text-center flex flex-col items-center justify-center">
                <div className="bg-stone-100 text-stone-400 p-4 rounded-full text-3xl mb-4">
                  <i className="fa-solid fa-circle-info" />
                </div>
                <h3 className="font-serif font-bold text-[#0f0d0e] text-xl">Ingen enheder fundet</h3>
                <p className="text-stone-500 text-sm mt-1 max-w-md">
                  Der er desværre ingen feriehuse eller hytter, der matcher dine valgte filtre. Prøv at nulstille eller ændre dine valg.
                </p>
                <button
                  onClick={() => {
                    setSelectedType('Alle');
                    setSelectedCapacity('Alle');
                    setSelectedFacilities([]);
                  }}
                  className="mt-6 bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-2.5 rounded-full text-sm transition-colors"
                >
                  Nulstil filtre
                </button>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {currentItems.map((acc) => {
                    const mainImg = acc.images?.[0] || 'https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/d88001-main-01.jpg';
                    const optimizedImg = mainImg.replace('https://assets.auxodev.dk/assets', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets');

                    return (
                      <div
                        key={acc.id}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-stone-200/60 transition-all duration-300 flex flex-col h-full"
                      >
                        <div className="h-52 overflow-hidden relative">
                          <img
                            src={optimizedImg}
                            alt={acc.title?.da || acc.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-4 right-4 bg-white/95 text-[#0f0d0e] text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                            <i className="fa-solid fa-users mr-1" /> Op til {acc.capacity} pers.
                          </div>
                        </div>
                        <div className="p-6 flex-grow flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className="text-xs font-bold text-[#67aedd] uppercase tracking-wider">
                                {acc.type?.da || acc.type}
                              </span>
                              {acc.size && (
                                <span className="text-xs text-stone-500">
                                  {acc.size}
                                </span>
                              )}
                            </div>
                            <h3 className="text-xl font-serif font-bold text-[#0f0d0e] mb-2">
                              {acc.title?.da || acc.title}
                            </h3>
                            <div
                              className="text-stone-600 text-xs leading-relaxed line-clamp-3 mb-4"
                              dangerouslySetInnerHTML={{ __html: acc.description?.da || acc.description }}
                            />
                          </div>
                          <div className="pt-4 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <span className="text-xs font-semibold text-stone-500">
                              {formatPrice(acc.price_info?.da || acc.price_info)}
                            </span>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              <button
                                onClick={() => openDetailModal(acc)}
                                className="flex-1 sm:flex-none text-center border border-[#67aedd] text-[#67aedd] hover:bg-[#67aedd]/5 font-bold px-3 py-2 rounded-full text-xs transition-colors"
                              >
                                Vis detaljer
                              </button>
                              {acc.novasol_link ? (
                                <a
                                  href={acc.novasol_link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex-1 sm:flex-none text-center bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-3.5 py-2 rounded-full text-xs transition-colors inline-flex items-center justify-center gap-1"
                                >
                                  Book nu
                                </a>
                              ) : (
                                <Link
                                  to={`/kontakt?enhed=${acc.slug?.da || acc.slug}`}
                                  className="flex-1 sm:flex-none text-center bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-3.5 py-2 rounded-full text-xs transition-colors inline-flex items-center justify-center gap-1"
                                >
                                  Book nu
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10 pt-6 border-t border-stone-200/60">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`flex items-center justify-center w-10 h-10 rounded-xl border border-stone-200 text-stone-600 transition-all ${
                        currentPage === 1
                          ? 'opacity-40 cursor-not-allowed bg-stone-50'
                          : 'hover:bg-stone-50 hover:border-stone-300 active:scale-95 cursor-pointer'
                      }`}
                      aria-label="Forrige side"
                    >
                      <i className="fa-solid fa-chevron-left text-xs" />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                          currentPage === pageNum
                            ? 'bg-[#67aedd] text-white shadow-sm'
                            : 'border border-stone-200 text-stone-600 hover:bg-stone-50 hover:border-stone-300 active:scale-95'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`flex items-center justify-center w-10 h-10 rounded-xl border border-stone-200 text-stone-600 transition-all ${
                        currentPage === totalPages
                          ? 'opacity-40 cursor-not-allowed bg-stone-50'
                          : 'hover:bg-stone-50 hover:border-stone-300 active:scale-95 cursor-pointer'
                      }`}
                      aria-label="Næste side"
                    >
                      <i className="fa-solid fa-chevron-right text-xs" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedAcc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 md:p-10 backdrop-blur-sm animate-fade-in"
          onClick={closeDetailModal}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e]">
                {selectedAcc.title?.da || selectedAcc.title}
              </h2>
              <button
                onClick={closeDetailModal}
                className="text-stone-400 hover:text-stone-600 text-2xl focus:outline-none p-2"
                aria-label="Luk vindue"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 md:p-8 flex flex-col gap-8">
              {/* Image Gallery Grid */}
              <div className="flex flex-col gap-3">
                {/* Main Image */}
                <div className="relative overflow-hidden rounded-2xl bg-stone-100 h-64 sm:h-80 md:h-[450px] group shadow-sm">
                  <img
                    src={(selectedAcc.images?.[activeImageIndex] || '').replace('https://assets.auxodev.dk/assets', 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets')}
                    alt={`${selectedAcc.title?.da || 'Enhed'} - Billede ${activeImageIndex + 1}`}
                    className="w-full h-full object-cover transition-all duration-500"
                  />
                  
                  {/* Navigation Arrows */}
                  {selectedAcc.images && selectedAcc.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setActiveImageIndex((prev) => (prev === 0 ? selectedAcc.images.length - 1 : prev - 1))}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-stone-800 hover:text-[#67aedd] w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all active:scale-95 cursor-pointer"
                        aria-label="Forrige billede"
                      >
                        <i className="fa-solid fa-chevron-left" />
                      </button>
                      <button
                        onClick={() => setActiveImageIndex((prev) => (prev === selectedAcc.images.length - 1 ? 0 : prev + 1))}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-stone-800 hover:text-[#67aedd] w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all active:scale-95 cursor-pointer"
                        aria-label="Næste billede"
                      >
                        <i className="fa-solid fa-chevron-right" />
                      </button>
                      
                      {/* Image Counter Badge */}
                      <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                        {activeImageIndex + 1} / {selectedAcc.images.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Thumbnails */}
                {selectedAcc.images && selectedAcc.images.length > 1 && (
                  <div className="flex items-center gap-2 overflow-x-auto py-1 no-scrollbar scroll-smooth">
                    {selectedAcc.images.map((imgUrl, idx) => {
                      const optimizedThumb = imgUrl.replace('https://assets.auxodev.dk/assets', 'https://assets.auxodev.dk/cdn-cgi/image/width=150,quality=80,format=auto/assets');
                      return (
                        <button
                          key={idx}
                          onClick={() => setActiveImageIndex(idx)}
                          className={`relative flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            idx === activeImageIndex
                              ? 'border-[#67aedd] ring-2 ring-[#67aedd]/20 scale-95'
                              : 'border-transparent opacity-70 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={optimizedThumb}
                            alt={`${selectedAcc.title?.da || 'Enhed'} - Miniature ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Specs Grid */}
              <div className={`grid grid-cols-2 sm:grid-cols-3 ${(selectedAcc.type?.da || selectedAcc.type) !== 'Event' && (selectedAcc.beds?.da || selectedAcc.beds) ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 bg-stone-50 p-5 rounded-2xl border border-stone-200/50 text-center`}>
                <div>
                  <span className="text-xs text-stone-500 block uppercase tracking-wider">Kapacitet</span>
                  <span className="font-bold text-[#0f0d0e] text-xs sm:text-sm md:text-base mt-1 block">
                    <i className="fa-solid fa-users mr-1.5 text-[#67aedd]" /> Op til {selectedAcc.capacity} personer
                  </span>
                </div>
                <div>
                  <span className="text-xs text-stone-500 block uppercase tracking-wider">Type</span>
                  <span className="font-bold text-[#0f0d0e] text-xs sm:text-sm md:text-base mt-1 block">
                    <i className="fa-solid fa-house-chimney mr-1.5 text-[#67aedd]" /> {selectedAcc.type?.da || selectedAcc.type}
                  </span>
                </div>
                {(selectedAcc.type?.da || selectedAcc.type) !== 'Event' && (selectedAcc.beds?.da || selectedAcc.beds) && (
                  <div>
                    <span className="text-xs text-stone-500 block uppercase tracking-wider">Senge</span>
                    <span className="font-bold text-[#0f0d0e] text-xs sm:text-sm md:text-base mt-1 block">
                      <i className="fa-solid fa-bed mr-1.5 text-[#67aedd]" /> {selectedAcc.beds?.da || selectedAcc.beds}
                    </span>
                  </div>
                )}
                {selectedAcc.size && (
                  <div>
                    <span className="text-xs text-stone-500 block uppercase tracking-wider">Størrelse</span>
                    <span className="font-bold text-[#0f0d0e] text-xs sm:text-sm md:text-base mt-1 block">
                      <i className="fa-solid fa-up-right-and-down-left-from-center mr-1.5 text-[#67aedd]" /> {selectedAcc.size}
                    </span>
                  </div>
                )}
                {selectedAcc.view && (
                  <div>
                    <span className="text-xs text-stone-500 block uppercase tracking-wider">Udsigt</span>
                    <span className="font-bold text-[#0f0d0e] text-xs sm:text-sm md:text-base mt-1 block">
                      <i className="fa-solid fa-water mr-1.5 text-[#67aedd]" /> {selectedAcc.view?.da || selectedAcc.view}
                    </span>
                  </div>
                )}
              </div>

              {/* Description & Facilities */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <h3 className="font-serif font-bold text-[#0f0d0e] text-xl mb-3">Beskrivelse</h3>
                  <div
                    className="text-stone-600 text-sm leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: selectedAcc.description?.da || selectedAcc.description }}
                  />
                </div>

                <div>
                  <h3 className="font-serif font-bold text-[#0f0d0e] text-xl mb-3">Faciliteter</h3>
                  <div className="flex flex-wrap gap-2">
                    {(selectedAcc.facilities?.da || selectedAcc.facilities || '').split(',').map((fac) => (
                      <span
                        key={fac}
                        className="bg-[#67aedd]/10 text-[#0f0d0e] border border-[#67aedd]/20 text-xs font-medium px-3 py-1.5 rounded-full"
                      >
                        <i className="fa-solid fa-check mr-1.5 text-[#67aedd]" /> {fac.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-stone-50 px-6 py-4 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <span className="text-sm sm:text-base font-semibold text-stone-700 text-center sm:text-left">
                Pris: {formatPrice(selectedAcc.price_info?.da || selectedAcc.price_info)}
              </span>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                {selectedAcc.novasol_link ? (
                  <a
                    href={selectedAcc.novasol_link}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-3 rounded-full text-sm shadow-sm transition-colors flex items-center justify-center gap-2 w-full sm:w-auto text-center"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" /> Book via Novasol
                  </a>
                ) : (
                  <Link
                    to={`/kontakt?enhed=${selectedAcc.slug?.da || selectedAcc.slug}`}
                    onClick={closeDetailModal}
                    className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-3 rounded-full text-sm shadow-sm transition-colors flex items-center justify-center gap-2 w-full sm:w-auto text-center"
                  >
                    <i className="fa-solid fa-envelope" /> Kontakt os for booking
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
